import React from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';
import { RegulatoryUpdate } from '../../services/dashboardService';

interface RegulatoryTimelineChartProps {
  updates: RegulatoryUpdate[];
}

const RegulatoryTimelineChart: React.FC<RegulatoryTimelineChartProps> = ({ updates }) => {
  const theme = useTheme();

  // Group updates by date and count them
  const timelineData = updates.reduce((acc, update) => {
    const date = new Date(update.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    
    if (!acc[date]) {
      acc[date] = {
        date,
        count: 0,
        high: 0,
        medium: 0,
        low: 0,
      };
    }
    
    acc[date].count += 1;
    acc[date][update.impact_level] += 1;
    
    return acc;
  }, {} as Record<string, any>);

  const chartData = Object.values(timelineData)
    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-7); // Last 7 days

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            border: `1px solid #2a2a2a`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Updates: {data.count}
          </Typography>
          {data.high > 0 && (
            <Typography variant="body2" color="error.main">
              High Impact: {data.high}
            </Typography>
          )}
          {data.medium > 0 && (
            <Typography variant="body2" color="warning.main">
              Medium Impact: {data.medium}
            </Typography>
          )}
          {data.low > 0 && (
            <Typography variant="body2" color="info.main">
              Low Impact: {data.low}
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={300}
        bgcolor="background.default"
        borderRadius={1}
        sx={{ border: '1px dashed', borderColor: 'divider' }}
      >
        <Typography variant="body1" color="text.secondary">
          No regulatory updates available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Regulatory Updates Timeline
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="date"
            fontSize={12}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            fontSize={12}
            stroke={theme.palette.text.secondary}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="count"
            stroke={theme.palette.primary.main}
            fill={theme.palette.primary.main}
            fillOpacity={0.3}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RegulatoryTimelineChart;
